/**
 * Global Settings Page - Email Recipients and User Management
 * Super User only access
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  Museum as MuseumIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import UserCreateForm, { CreateUserData } from '../components/UserCreateForm';
import UserEditForm, { UpdateUserData } from '../components/UserEditForm';
import PasswordChangeForm from '../components/PasswordChangeForm';
import { UserTable } from '../modules/user';
import { User } from '../modules/user/types';
import { apiService as api } from '../shared/services/api';
import { fetchDashboardUserLogs } from '../shared/services/logging';
import MuseumPage from './MuseumPage';

interface EmailRecipient {
  id: number;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ReportTemplate {
  id: number;
  report_type: string;
  museum: string;
  name?: string;
}

interface ReportSchedule {
  id: number;
  template: number | ReportTemplate;
  frequency: string;
  is_active: boolean;
  template_data?: ReportTemplate;
}

interface DashboardLog {
  id?: number;
  ts?: string;
  role?: string;
  robot?: string;
  museum?: string;
  task?: string;
  log?: Record<string, unknown>;
}

const getLogValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
};

const formatLogAction = (action?: string): string => {
  if (!action) return 'dashboard user action';
  return action.replace(/_/g, ' ');
};

const getLogMetadata = (entry: DashboardLog) => {
  const details = entry.log ?? {};
  const action = getLogValue(details.action) ?? entry.task ?? 'dashboard_user_action';
  const museum = getLogValue(entry.museum) ?? getLogValue(details.museum_code) ?? 'NIL';
  const robot = getLogValue(entry.robot) ?? getLogValue(details.robot_id) ?? getLogValue(details.robot_name) ?? 'NIL';
  const user = getLogValue(details.username) ?? getLogValue(details.user_id) ?? undefined;
  const role = getLogValue(details.user_role) ?? entry.role ?? 'N/A';
  const timestamp = entry.ts ? new Date(entry.ts).toLocaleString() : 'Unknown time';

  const knownKeys = new Set(['action', 'user_id', 'username', 'user_role', 'robot_id', 'robot_name', 'museum_code']);

  const extraDetails = Object.fromEntries(
    Object.entries(details).filter(([key, value]) => !knownKeys.has(key) && value !== undefined),
  );

  return {
    action,
    museum,
    robot,
    user,
    role,
    timestamp,
    extraDetails,
  };
};

const normalizeResults = <T,>(data: T[] | { results?: T[] }): T[] => {
  if (Array.isArray(data)) {
    return data;
  }
  return data.results ?? [];
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return fallback;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector(state => state.auth);

  // State management
  const [tabValue, setTabValue] = useState(0);
  const [emailRecipients, setEmailRecipients] = useState<EmailRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // User Management state
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Dashboard logs state
  const [dashboardLogs, setDashboardLogs] = useState<DashboardLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Periodic Reports Configuration state
  const [periodicConfig, setPeriodicConfig] = useState({
    frequency: 'month',
    fileFormat: 'pdf',
  });

  // Handle periodic reports configuration save
  const handleSavePeriodicConfig = async () => {
    try {
      setLoading(true);

      // Step 1: Create or update ReportTemplate
      const templateData = {
        name: `${periodicConfig.frequency.charAt(0).toUpperCase() + periodicConfig.frequency.slice(1)} Periodic Report`,
        report_type: 'periodic_summary',
        museum: 'ACM', // Default museum - could be made configurable
        description: `Automated ${periodicConfig.frequency} report generation`,
        is_active: true,
      };

      // Check if template already exists
      const existingTemplates = await api.get<ReportTemplate[] | { results?: ReportTemplate[] }>('/reports/templates/');
      const templates = normalizeResults(existingTemplates);
      let template = templates.find(t => t.report_type === 'periodic_summary' && t.museum === 'ACM');

      if (template) {
        // Update existing template
        template = await api.put<ReportTemplate>(`/reports/templates/${template.id}/`, templateData);
      } else {
        // Create new template
        template = await api.post<ReportTemplate>('/reports/templates/', templateData);
      }

      if (!template) {
        throw new Error('Failed to create or update report template');
      }

      // Step 2: Create or update ReportSchedule
      const scheduleData = {
        template: template.id,
        frequency: periodicConfig.frequency,
        is_active: true,
        next_run: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Tomorrow
      };

      // Check if schedule already exists for this template
      const existingSchedules = await api.get<ReportSchedule[] | { results?: ReportSchedule[] }>('/reports/schedules/');
      const schedules = normalizeResults(existingSchedules);
      let schedule = schedules.find(s => s.template === template!.id);

      if (schedule) {
        // Update existing schedule
        schedule = await api.put(`/reports/schedules/${schedule.id}/`, scheduleData);
      } else {
        // Create new schedule
        schedule = await api.post('/reports/schedules/', scheduleData);
      }

      console.log('Periodic config saved:', { template, schedule, config: periodicConfig });

      setSuccess(
        `Periodic reports configuration saved successfully! ${periodicConfig.frequency.charAt(0).toUpperCase() + periodicConfig.frequency.slice(1)} reports will be generated in ${periodicConfig.fileFormat.toUpperCase()} format.`,
      );
      setTimeout(() => setSuccess(null), 5000);
    } catch (error) {
      console.error('Error saving periodic config:', error);
      setError('Failed to save periodic reports configuration');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Load existing periodic configuration
  const loadPeriodicConfig = async () => {
    try {
      const schedules = await api.get<ReportSchedule[] | { results?: ReportSchedule[] }>('/reports/schedules/');
      const scheduleList = normalizeResults(schedules);
      const periodicSchedule = scheduleList.find(
        s =>
          (typeof s.template === 'object' ? s.template?.report_type : s.template_data?.report_type) ===
            'periodic_summary' && s.is_active,
      );

      if (periodicSchedule) {
        setPeriodicConfig({
          frequency: periodicSchedule.frequency,
          fileFormat: 'pdf', // Default format, could be stored in template
        });
      }
    } catch (error) {
      console.error('Error loading periodic config:', error);
    }
  };

  // Check if user is Admin
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadEmailRecipients();
    loadPeriodicConfig();
  }, [isAdmin, navigate]);

  useEffect(() => {
    const tabParam = new URLSearchParams(location.search).get('tab');
    if (!tabParam) return;
    const tabMap: Record<string, number> = {
      email: 0,
      users: 1,
      reports: 2,
      museums: 3,
      logs: 4,
    };
    const resolvedTab = tabMap[tabParam.toLowerCase()];
    if (resolvedTab !== undefined && resolvedTab !== tabValue) {
      setTabValue(resolvedTab);
    }
  }, [location.search, tabValue]);

  const loadEmailRecipients = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/alerts/email-recipients/', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load email recipients');
      }
      const data = await response.json();
      setEmailRecipients(data.results || []);
    } catch (err) {
      setError('Failed to load email recipients');
    } finally {
      setLoading(false);
    }
  };

  const addEmailRecipient = async () => {
    if (!newEmail.trim()) {
      setError('Email address is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(newEmail.trim())) {
      setError('Invalid email format');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/alerts/email-recipients/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newEmail.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add email recipient');
      }

      setSuccess(`Email recipient ${newEmail} added successfully`);
      setNewEmail('');
      setAddDialogOpen(false);
      loadEmailRecipients();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to add email recipient'));
    } finally {
      setLoading(false);
    }
  };

  const removeEmailRecipient = async (id: number, email: string) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from the alert recipients?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/alerts/email-recipients/${id}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove email recipient');
      }

      setSuccess(`Email recipient ${email} removed successfully`);
      loadEmailRecipients();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to remove email recipient'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // User Management functions
  const fetchUsers = useCallback(async () => {
    try {
      setIsLoadingUsers(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/users/', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data: unknown = await response.json();
      setUsers(data as User[]);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to fetch users'));
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const handleCreateUser = async (userData: CreateUserData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/users/create/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create user');
      }

      setSuccess('User created successfully');
      await fetchUsers();
      setCreateDialogOpen(false);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to create user'));
    }
  };

  const handleUpdateUser = async (userData: UpdateUserData) => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/auth/users/${selectedUser.id}/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user');
      }

      setSuccess('User updated successfully');
      await fetchUsers();
      setEditDialogOpen(false);
      setSelectedUser(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to update user'));
    }
  };

  const handleChangePassword = async (oldPassword: string | null, newPassword: string, newPasswordConfirm: string) => {
    if (!selectedUser) return;
    const isSelfChange = !!user && selectedUser.id === user.id;
    try {
      if (isSelfChange) {
        await api.changeOwnPassword({
          old_password: oldPassword || '',
          new_password: newPassword,
          new_password_confirm: newPasswordConfirm,
        });
      } else {
        await api.changeOtherUserPassword(selectedUser.id, newPassword, newPasswordConfirm);
      }
      setSuccess('Password changed successfully');
      setPasswordDialogOpen(false);
      setSelectedUser(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to change password'));
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/auth/users/${selectedUser.id}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }

      setSuccess('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      await fetchUsers();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to delete user'));
    }
  };

  // Load users when User Management tab is selected
  useEffect(() => {
    if (tabValue === 1 && users.length === 0) {
      fetchUsers();
    }
  }, [tabValue, users.length, fetchUsers]);

  const loadDashboardLogs = useCallback(async () => {
    try {
      setIsLoadingLogs(true);
      const data = await fetchDashboardUserLogs({ limit: 100, offset: 0, task: 'dashboard_user_action' });
      if (Array.isArray(data)) {
        setDashboardLogs(data as DashboardLog[]);
      } else {
        const logs = (data as { logs?: DashboardLog[] }).logs;
        setDashboardLogs(logs ?? []);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load dashboard logs'));
    } finally {
      setIsLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 4 && dashboardLogs.length === 0) {
      loadDashboardLogs();
    }
  }, [tabValue, dashboardLogs.length, loadDashboardLogs]);

  // Redirect if not Admin
  if (!isAdmin) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 2, fontSize: 32 }} />
        <Typography variant="h4">System Settings</Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
          <Tab
            icon={<EmailIcon />}
            label="Email Recipients"
            iconPosition="start"
            id="settings-tab-0"
            aria-controls="settings-tabpanel-0"
          />
          <Tab
            icon={<PersonIcon />}
            label="User Management"
            iconPosition="start"
            id="settings-tab-1"
            aria-controls="settings-tabpanel-1"
          />
          <Tab
            icon={<ScheduleIcon />}
            label="Periodic Reports"
            iconPosition="start"
            id="settings-tab-2"
            aria-controls="settings-tabpanel-2"
          />
          <Tab
            icon={<MuseumIcon />}
            label="Museums"
            iconPosition="start"
            id="settings-tab-3"
            aria-controls="settings-tabpanel-3"
          />
          <Tab
            icon={<ListAltIcon />}
            label="Dashboard Logs"
            iconPosition="start"
            id="settings-tab-4"
            aria-controls="settings-tabpanel-4"
          />
        </Tabs>
      </Box>

      {/* Email Recipients Tab */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Email Recipients Configuration</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{ borderRadius: '8px' }}
              >
                Add Recipient
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Manage email addresses that will receive notifications from both Alert System and Report Scheduler.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Unified Email Configuration:</strong> These email recipients will be used for:
              </Typography>
              <Box sx={{ mt: 1, mb: 0 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                  <Box component="span" sx={{ mr: 1, minWidth: '8px' }}>
                    •
                  </Box>
                  <Box component="span">Alert notifications from the monitoring system</Box>
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ mr: 1, minWidth: '8px' }}>
                    •
                  </Box>
                  <Box component="span">Scheduled report notifications from the report scheduler</Box>
                </Typography>
              </Box>
            </Alert>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                {emailRecipients.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: '8px' }}>
                    No email recipients configured. Add recipients to receive alert and report notifications.
                  </Alert>
                ) : (
                  <List disablePadding>
                    {emailRecipients.map((recipient, index) => (
                      <React.Fragment key={recipient.id}>
                        <ListItem
                          sx={{
                            borderRadius: '8px',
                            mb: 1,
                            backgroundColor: theme.palette.grey[50],
                          }}
                        >
                          <EmailIcon sx={{ mr: 2, color: 'text.secondary' }} />
                          <ListItemText
                            primary={recipient.email}
                            secondary={`Added: ${new Date(recipient.created_at).toLocaleDateString()}`}
                          />
                          <Chip
                            label={recipient.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={recipient.is_active ? 'success' : 'default'}
                            sx={{ mr: 1 }}
                          />
                          <Tooltip title="Remove recipient">
                            <IconButton
                              edge="end"
                              onClick={() => removeEmailRecipient(recipient.id, recipient.email)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItem>
                        {index < emailRecipients.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* User Management Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                User Management
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{ borderRadius: '8px' }}
              >
                Create User
              </Button>
            </Box>

            <UserTable
              users={users}
              loading={isLoadingUsers}
              onEditUser={user => {
                setSelectedUser(user);
                setEditDialogOpen(true);
              }}
              onChangePassword={user => {
                setSelectedUser(user);
                setPasswordDialogOpen(true);
              }}
              onDeleteUser={user => {
                setSelectedUser(user);
                setDeleteDialogOpen(true);
              }}
            />
          </CardContent>
        </Card>
      </TabPanel>

      {/* Periodic Reports Configuration Tab */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Periodic Reports Configuration</Typography>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{ borderRadius: '8px' }}
                onClick={handleSavePeriodicConfig}
                disabled={loading}
              >
                Save Configuration
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure automatic report generation and email delivery schedules. Reports will be generated and sent to
              the configured email recipients.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Frequency Selection */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ minWidth: 120 }}>
                  Frequency:
                </Typography>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={periodicConfig.frequency}
                    label="Frequency"
                    onChange={e => {
                      setPeriodicConfig(prev => ({
                        ...prev,
                        frequency: e.target.value,
                      }));
                    }}
                  >
                    <MenuItem value="day">day</MenuItem>
                    <MenuItem value="week">week</MenuItem>
                    <MenuItem value="month">month</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* File Format */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ minWidth: 120 }}>
                  File Format:
                </Typography>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>File Format</InputLabel>
                  <Select
                    value={periodicConfig.fileFormat}
                    label="File Format"
                    onChange={e => {
                      setPeriodicConfig(prev => ({
                        ...prev,
                        fileFormat: e.target.value,
                      }));
                    }}
                  >
                    <MenuItem value="csv">CSV</MenuItem>
                    <MenuItem value="pdf">PDF</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Museums Tab */}
      <TabPanel value={tabValue} index={3}>
        <MuseumPage />
      </TabPanel>

      {/* Dashboard Logs Tab */}
      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Dashboard User Action Logs</Typography>
              <Button variant="outlined" onClick={loadDashboardLogs} disabled={isLoadingLogs}>
                Refresh
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing latest 100 logs for task <strong>dashboard_user_action</strong>.
            </Typography>

            {isLoadingLogs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : dashboardLogs.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: '8px' }}>
                No dashboard logs found.
              </Alert>
            ) : (
              <List disablePadding>
                {dashboardLogs.map((log, index) => {
                  const { action, museum, robot, user, role, timestamp, extraDetails } = getLogMetadata(log);
                  const hasExtraDetails = Object.keys(extraDetails).length > 0;
                  return (
                    <React.Fragment key={`${log.id ?? 'log'}-${index}`}>
                      <ListItem
                        sx={{
                          borderRadius: '8px',
                          mb: 1,
                          backgroundColor: theme.palette.grey[50],
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                              <Chip label={formatLogAction(action)} size="small" color="primary" />
                              <Chip label={`Museum: ${museum}`} size="small" variant="outlined" />
                              <Chip label={`Robot: ${robot}`} size="small" variant="outlined" />
                              {user && <Chip label={`User: ${user}`} size="small" variant="outlined" />}
                              {role && <Chip label={`Role: ${role}`} size="small" variant="outlined" />}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                                {timestamp}
                              </Typography>
                              {hasExtraDetails ? (
                                <Typography
                                  variant="caption"
                                  component="pre"
                                  sx={{
                                    mt: 0.5,
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'Monospace, monospace',
                                    backgroundColor: theme.palette.grey[100],
                                    borderRadius: '6px',
                                    p: 1,
                                  }}
                                >
                                  {JSON.stringify(extraDetails, null, 2)}
                                </Typography>
                              ) : (
                                <Typography variant="caption" component="span">
                                  No additional details
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                      {index < dashboardLogs.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Add Email Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Email Recipient</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="recipient@example.com"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={addEmailRecipient}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading || !newEmail.trim()}
          >
            {loading ? 'Adding...' : 'Add Recipient'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Management Dialogs */}
      {/* Create User Dialog */}
      <UserCreateForm open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} onSubmit={handleCreateUser} />

      {/* Edit User Dialog */}
      <UserEditForm
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleUpdateUser}
        user={selectedUser}
      />

      {/* Change Password Dialog */}
      <PasswordChangeForm
        open={passwordDialogOpen}
        onClose={() => {
          setPasswordDialogOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleChangePassword}
        userName={selectedUser?.username || ''}
        isSelfChange={Boolean(user && selectedUser && selectedUser.id === user.id)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user &quot;{selectedUser?.username}&quot;? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
